import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Modal,
  Typography,
} from "@mui/material";
import PrintDisabledIcon from "@mui/icons-material/PrintDisabled";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import DownloadIcon from "@mui/icons-material/Download";
import GitHubIcon from "@mui/icons-material/GitHub";
import BlockIcon from "@mui/icons-material/Block";

const GITHUB_RELEASES_URL =
  "https://github.com/slapie-delivery/printer-releases/releases";
const GITHUB_API_URL =
  "https://api.github.com/repos/slapie-delivery/printer-releases/releases/latest";

const PrinterErrorModal = ({
  open,
  onClose,
  errorMessage,
  onManualReconnect,
  onIgnorePrinter,
}) => {
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [latestRelease, setLatestRelease] = useState(null);
  const [isFetchingRelease, setIsFetchingRelease] = useState(false);
  const [showIgnoreConfirmDialog, setShowIgnoreConfirmDialog] = useState(false);

  const isReconnecting = errorMessage?.includes("Tentando reconectar");
  const isMaxAttemptsReached = errorMessage?.includes("deu errado");
  const isPersistentReconnecting = errorMessage?.includes(
    "Verificando conexão a cada"
  );
  const hasAttemptNumber = /Tentativa \d+\/\d+/.test(errorMessage);

  useEffect(() => {
    const shouldCheck = open && isMaxAttemptsReached;

    if (shouldCheck) {
      checkWebSocketConnection();
      fetchLatestRelease();
    }
  }, [open, isMaxAttemptsReached]);

  const checkWebSocketConnection = async () => {
    setIsCheckingConnection(true);
    setConnectionStatus(null);

    try {
      const ws = new WebSocket("ws://localhost:3020");

      const timeoutId = setTimeout(() => {
        ws.close();
        setConnectionStatus("error");
        setIsCheckingConnection(false);
      }, 3000);

      ws.onopen = () => {
        clearTimeout(timeoutId);
        ws.close();
        setConnectionStatus("success");
        setIsCheckingConnection(false);
      };

      ws.onerror = () => {
        clearTimeout(timeoutId);
        setConnectionStatus("error");
        setIsCheckingConnection(false);
      };
    } catch (error) {
      setConnectionStatus("error");
      setIsCheckingConnection(false);
    }
  };

  const fetchLatestRelease = async () => {
    setIsFetchingRelease(true);

    try {
      const response = await fetch(GITHUB_API_URL);

      if (!response.ok) {
        throw new Error("Failed to fetch release");
      }

      const data = await response.json();

      const nsisAsset = data.assets?.find(
        (asset) =>
          asset.name.endsWith(".exe") && !asset.name.includes("portable")
      );

      const portableAsset = data.assets?.find(
        (asset) =>
          asset.name.includes("portable") && asset.name.endsWith(".exe")
      );

      setLatestRelease({
        version: data.tag_name,
        releaseUrl: data.html_url,
        downloadUrl: nsisAsset?.browser_download_url || null,
        portableUrl: portableAsset?.browser_download_url || null,
        publishedAt: data.published_at,
      });
    } catch (error) {
      console.error("Error fetching latest release:", error);
      setLatestRelease(null);
    } finally {
      setIsFetchingRelease(false);
    }
  };

  const getTitle = () => {
    if (isReconnecting) {
      return "Reconectando à Impressora...";
    }

    if (isMaxAttemptsReached) {
      return "Falha na Conexão";
    }

    return "Impressora Desconectada";
  };

  const getDescription = () => {
    if (isReconnecting) {
      return "Tentando restabelecer a conexão com o serviço de impressão.";
    }

    if (isMaxAttemptsReached) {
      return "Não foi possível conectar ao serviço de impressão após várias tentativas.";
    }

    return "A conexão com o serviço de impressão foi perdida.";
  };

  const handleOpenApp = () => {
    try {
      window.open("slapie://open", "_blank");

      setTimeout(() => {
        checkWebSocketConnection();
      }, 2000);
    } catch (error) {
      console.error("Erro ao abrir o app:", error);
      alert(
        "Não foi possível abrir o app automaticamente. Por favor, abra manualmente o aplicativo Slapie no Windows."
      );
    }
  };

  const handleDownload = (url) => {
    if (!url) {
      window.open(GITHUB_RELEASES_URL, "_blank");
      return;
    }

    window.open(url, "_blank");
  };

  const handleOpenReleases = () => {
    window.open(GITHUB_RELEASES_URL, "_blank");
  };

  const handleOpenIgnoreDialog = () => {
    setShowIgnoreConfirmDialog(true);
  };

  const handleCloseIgnoreDialog = () => {
    setShowIgnoreConfirmDialog(false);
  };

  const handleConfirmIgnore = () => {
    setShowIgnoreConfirmDialog(false);
    onIgnorePrinter();
  };

  const title = getTitle();
  const description = getDescription();
  const canClose = !isReconnecting || isPersistentReconnecting;
  const showConnectionTest = isMaxAttemptsReached || connectionStatus !== null;
  const appNotInstalled = connectionStatus === "error" && isMaxAttemptsReached;

  return (
    <>
      <Modal
        open={open}
        onClose={canClose ? onClose : undefined}
        aria-labelledby="printer-error-title"
        disableEscapeKeyDown={!canClose}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 550,
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
            }}
          >
            {isReconnecting ? (
              <CircularProgress
                size={64}
                sx={{
                  color: "warning.main",
                  mb: 2,
                }}
              />
            ) : (
              <IconButton
                sx={{
                  color: "error.main",
                  fontSize: 64,
                  mb: 2,
                }}
                disableRipple
              >
                <PrintDisabledIcon fontSize="inherit" />
              </IconButton>
            )}

            <Typography
              id="printer-error-title"
              variant="h5"
              component="h2"
              sx={{
                fontWeight: "bold",
                mb: 1,
                textAlign: "center",
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              {description}
            </Typography>
          </Box>

          {hasAttemptNumber && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Status da Reconexão</AlertTitle>
              {errorMessage}
            </Alert>
          )}

          {isPersistentReconnecting && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Reconexão Automática Ativa</AlertTitle>O sistema está
              verificando a conexão automaticamente. Você pode fechar esta
              janela e voltar a trabalhar - a reconexão acontecerá em segundo
              plano.
            </Alert>
          )}

          {showConnectionTest && (
            <Box sx={{ mb: 2 }}>
              <Alert
                severity={
                  connectionStatus === "success"
                    ? "success"
                    : connectionStatus === "error"
                    ? "error"
                    : "info"
                }
                icon={
                  isCheckingConnection ? (
                    <CircularProgress size={20} />
                  ) : connectionStatus === "success" ? (
                    <CheckCircleIcon />
                  ) : connectionStatus === "error" ? (
                    <ErrorIcon />
                  ) : null
                }
              >
                <AlertTitle>
                  {isCheckingConnection
                    ? "Verificando conexão..."
                    : connectionStatus === "success"
                    ? "✓ App está rodando!"
                    : connectionStatus === "error"
                    ? "✗ App não está rodando"
                    : "Verificar se o app está rodando"}
                </AlertTitle>
                {connectionStatus === "success" && (
                  <Typography variant="body2">
                    O serviço de impressão está ativo. Aguarde a reconexão
                    automática ou clique em "Fechar" e tente imprimir novamente.
                  </Typography>
                )}
                {connectionStatus === "error" && (
                  <Typography variant="body2">
                    O serviço de impressão não está respondendo. Abra o
                    aplicativo Slapie ou faça o download se ainda não tiver
                    instalado.
                  </Typography>
                )}
              </Alert>
            </Box>
          )}

          {appNotInstalled && latestRelease && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" icon={<GitHubIcon />}>
                <AlertTitle>Download Disponível</AlertTitle>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Versão mais recente: <strong>{latestRelease.version}</strong>
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {latestRelease.downloadUrl && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(latestRelease.downloadUrl)}
                    >
                      Instalador
                    </Button>
                  )}
                  {latestRelease.portableUrl && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(latestRelease.portableUrl)}
                    >
                      Portátil
                    </Button>
                  )}
                </Box>
              </Alert>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "bold",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
              Como resolver?
            </Typography>

            <Box component="ol" sx={{ pl: 2, m: 0 }}>
              {appNotInstalled ? (
                <>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    <strong>Baixe e instale</strong> o aplicativo Slapie
                    clicando no botão de download abaixo
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Execute o instalador e siga as instruções na tela
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Após a instalação, abra o aplicativo Slapie
                  </Typography>
                  <Typography component="li" variant="body2">
                    Aguarde alguns segundos para a conexão automática
                  </Typography>
                </>
              ) : (
                <>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Clique no botão <strong>"Tentar Abrir App Slapie"</strong>{" "}
                    abaixo
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Se não funcionar, procure o ícone do <strong>Slapie</strong>{" "}
                    na barra de tarefas do Windows (canto inferior direito)
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Ou procure por <strong>"Slapie"</strong> no menu Iniciar do
                    Windows e abra o aplicativo
                  </Typography>
                  <Typography component="li" variant="body2">
                    Aguarde alguns segundos após abrir o app para a reconexão
                    automática
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {isMaxAttemptsReached && connectionStatus === "error" && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>
                {appNotInstalled
                  ? "⚠ App não instalado"
                  : "⚠ App não está rodando"}
              </AlertTitle>
              {appNotInstalled ? (
                <>
                  O aplicativo Slapie não está instalado ou não foi encontrado.
                  Faça o download e instale para usar o serviço de impressão.
                </>
              ) : (
                <>
                  O aplicativo Slapie precisa estar aberto e rodando para que as
                  impressões funcionem. Por favor, abra o aplicativo e aguarde a
                  reconexão.
                </>
              )}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: "column",
            }}
          >
            {connectionStatus !== "success" && (
              <>
                {!appNotInstalled && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenApp}
                    startIcon={<OpenInNewIcon />}
                    fullWidth
                  >
                    Tentar Abrir App Slapie
                  </Button>
                )}

                {appNotInstalled && (
                  <>
                    {isFetchingRelease ? (
                      <Button
                        variant="contained"
                        color="primary"
                        disabled
                        startIcon={<CircularProgress size={20} />}
                        fullWidth
                      >
                        Buscando versão...
                      </Button>
                    ) : latestRelease?.downloadUrl ? (
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() =>
                          handleDownload(latestRelease.downloadUrl)
                        }
                        startIcon={<DownloadIcon />}
                        fullWidth
                      >
                        Baixar App Slapie ({latestRelease.version})
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenReleases}
                        startIcon={<GitHubIcon />}
                        fullWidth
                      >
                        Ir para Página de Downloads
                      </Button>
                    )}
                  </>
                )}

                {isMaxAttemptsReached && (
                  <>
                    {isPersistentReconnecting && onManualReconnect && (
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={onManualReconnect}
                        startIcon={<RefreshIcon />}
                        fullWidth
                      >
                        Tentar Reconectar Agora
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={checkWebSocketConnection}
                      startIcon={
                        isCheckingConnection ? (
                          <CircularProgress size={20} />
                        ) : (
                          <RefreshIcon />
                        )
                      }
                      disabled={isCheckingConnection}
                      fullWidth
                    >
                      {isCheckingConnection
                        ? "Verificando..."
                        : "Verificar se App está Rodando"}
                    </Button>
                  </>
                )}
              </>
            )}

            {canClose && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={onClose}
                fullWidth
              >
                Fechar
              </Button>
            )}

            <Divider sx={{ my: 1 }} />

            <Button
              variant="text"
              color="error"
              onClick={handleOpenIgnoreDialog}
              startIcon={<BlockIcon />}
              fullWidth
              size="small"
            >
              Ignorar Impressora Permanentemente
            </Button>
          </Box>

          {isReconnecting && !isPersistentReconnecting && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                color: "text.secondary",
                mt: 2,
              }}
            >
              Esta janela fechará automaticamente quando a conexão for
              restabelecida
            </Typography>
          )}

          {isPersistentReconnecting && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                color: "text.secondary",
                mt: 2,
              }}
            >
              Você pode fechar esta janela - o sistema continuará tentando
              reconectar em segundo plano
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Problemas para encontrar o app?
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Pressione <Chip label="⊞ Win" size="small" /> + digite "Slapie"
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 1 }}
            >
              <Link
                href={GITHUB_RELEASES_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: "none" }}
              >
                Ver todas as versões →
              </Link>
            </Typography>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={showIgnoreConfirmDialog}
        onClose={handleCloseIgnoreDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BlockIcon color="error" />
            Ignorar Conexão com Impressora?
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ao ignorar a conexão com a impressora, você:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 2, color: "text.secondary" }}>
            <li>
              <Typography variant="body2">
                <strong>Não receberá mais alertas</strong> sobre conexão com a
                impressora
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Não poderá imprimir</strong> até reativar a conexão
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                As tentativas de reconexão automática serão{" "}
                <strong>desativadas</strong>
              </Typography>
            </li>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <AlertTitle>Atenção</AlertTitle>
            Esta ação pode ser revertida nas configurações do sistema a qualquer
            momento.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseIgnoreDialog} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmIgnore}
            color="error"
            variant="contained"
            startIcon={<BlockIcon />}
          >
            Sim, Ignorar Impressora
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrinterErrorModal;
